import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { WorkspaceDirectoryLayoutMode } from '@/components/files/workspace-directory-view-model';

const NAME_WIDTHS = ['w-20', 'w-14', 'w-16'] as const;
const ROW_NAME_WIDTHS = ['w-40', 'w-56', 'w-28'] as const;

/**
 * Placeholder rows in the shape the folder will open in: the icon grid's
 * tiles or the list's rows, on the same grid and insets as the real entries
 * (`workspace-directory-icon-entries` / `-row-entries`), so the content takes
 * their place without a jump. Used by the page skeleton and while a folder's
 * first visible rows are still on their way.
 */
export function WorkspaceDirectoryEntriesSkeleton({
  layout,
  compact = false,
  count = 12,
  className,
}: {
  layout: WorkspaceDirectoryLayoutMode;
  compact?: boolean;
  count?: number;
  className?: string;
}) {
  if (layout === 'list') {
    return (
      <div aria-hidden="true" className={cn('px-3 py-1', className)}>
        {Array.from({ length: Math.min(count, 9) }).map((_, index) => (
          <div key={index} className="flex h-11 items-center gap-3 px-2">
            <Skeleton className="size-5 shrink-0 rounded-md" />
            <Skeleton
              className={cn(
                'h-3',
                ROW_NAME_WIDTHS[index % ROW_NAME_WIDTHS.length]
              )}
            />
            <Skeleton className="ms-auto h-3 w-16" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className={cn(
        'grid content-start items-start',
        compact
          ? 'grid-cols-[repeat(auto-fill,minmax(92px,1fr))] gap-x-1 gap-y-2 p-2'
          : 'grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-x-2 gap-y-3 px-3 py-3',
        className
      )}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex flex-col items-center gap-2 py-2">
          <Skeleton className="size-14 rounded-[14px]" />
          <Skeleton
            className={cn('h-3', NAME_WIDTHS[index % NAME_WIDTHS.length])}
          />
        </div>
      ))}
    </div>
  );
}
